import type { GameState } from "@dailywars/core/engine";

import { ATTACK_RANGE_FLASH_PERIOD_MS } from "#/game/animation/constants";
import {
  ATTACK_RANGE_FLASH_ICON_SIZE,
  getAttackRangeFlashIconImage,
} from "#/game/attackRangeFlashIcon";
import { isImageReady } from "#/game/imageReady";

import {
  getAttackRangeFlashDrawState,
  syncAttackRangeFlashAnimation,
  type AttackRangeFlashOrigin,
} from "./attackRangeFlashAnim";
import { isAnimationComplete } from "./boardAnimations";
import { TILE_SIZE, type CanvasViewState } from "./boardConstants";

const FLASH_MIN_ALPHA = 0.28;
const ORIGIN_BELOW_OFFSET_PX = 8;

export function attackRangeFlashOpacity({
  nowMs,
  pulseStartedAtMs,
}: {
  nowMs: number;
  pulseStartedAtMs: number | null;
}): number {
  if (pulseStartedAtMs === null) return 1;
  const elapsed = Math.max(0, nowMs - pulseStartedAtMs);
  const phase = (elapsed % ATTACK_RANGE_FLASH_PERIOD_MS) / ATTACK_RANGE_FLASH_PERIOD_MS;
  const wave = 0.5 + 0.5 * Math.cos(phase * Math.PI * 2);
  return FLASH_MIN_ALPHA + (1 - FLASH_MIN_ALPHA) * wave;
}

function resolveFlashOriginTile({
  state,
  viewState,
}: {
  state: GameState;
  viewState: CanvasViewState;
}): AttackRangeFlashOrigin | null {
  const unitId = viewState.selectedUnitId;
  if (!unitId) return null;
  if (viewState.previewUnit?.unitId === unitId) {
    return { ...viewState.previewUnit.pos };
  }
  const unit = state.units.find((candidate) => candidate.id === unitId);
  if (!unit) return null;
  return { ...unit.pos };
}

function originPixel(origin: AttackRangeFlashOrigin): { x: number; y: number } {
  return {
    x: origin.x * TILE_SIZE + TILE_SIZE / 2,
    y: origin.y * TILE_SIZE + TILE_SIZE / 2 + ORIGIN_BELOW_OFFSET_PX,
  };
}

function parseTileKey(key: string): AttackRangeFlashOrigin | null {
  const [xText, yText] = key.split(",");
  const x = Number(xText);
  const y = Number(yText);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

function drawFlashIcon({
  ctx,
  cx,
  cy,
  alpha,
  scale,
  image,
}: {
  ctx: CanvasRenderingContext2D;
  cx: number;
  cy: number;
  alpha: number;
  scale: number;
  image: HTMLImageElement;
}): void {
  const size = ATTACK_RANGE_FLASH_ICON_SIZE * scale;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.drawImage(image, cx - size / 2, cy - size / 2, size, size);
  ctx.restore();
}

export function drawAttackRangeFlashLayer({
  ctx,
  state,
  viewState,
  nowMs,
}: {
  ctx: CanvasRenderingContext2D;
  state: GameState;
  viewState: CanvasViewState;
  nowMs: number;
}): void {
  const previewAnimation = viewState.previewAnimation;
  const readyToExpand = previewAnimation === null || isAnimationComplete(previewAnimation, nowMs);

  syncAttackRangeFlashAnimation({
    origin: resolveFlashOriginTile({ state, viewState }),
    tileKeys: viewState.attackRangeFlashTileKeys,
    nowMs,
    readyToExpand,
  });

  const drawState = getAttackRangeFlashDrawState(nowMs);
  if (!drawState || drawState.travel <= 0) return;

  const image = getAttackRangeFlashIconImage(viewState.attackRangeFlashStyle);
  if (!isImageReady(image)) return;

  const travel = drawState.travel;
  const alpha = attackRangeFlashOpacity({
    nowMs,
    pulseStartedAtMs: drawState.pulseStartedAtMs,
  });
  const scale = 0.45 + 0.55 * travel;
  const from = originPixel(drawState.origin);

  for (const key of drawState.tileKeys) {
    const tile = parseTileKey(key);
    if (!tile) continue;
    const toX = tile.x * TILE_SIZE + TILE_SIZE / 2;
    const toY = tile.y * TILE_SIZE + TILE_SIZE / 2;
    drawFlashIcon({
      ctx,
      cx: from.x + (toX - from.x) * travel,
      cy: from.y + (toY - from.y) * travel,
      alpha,
      scale,
      image,
    });
  }
}
